import { isEmpty, partition } from 'lodash';
import Link from 'next/link';
import { useSelector } from 'react-redux';
import { trackSelectFromSearchSuggestion } from '../../common/analytics/analyticsTracker';
import { searchPageUrlGenerator } from '../../common/helpers/routing/urlGenerator';
import { handleImageUrl } from '../../common/helpers/sharpie/imageUrlHelper';
import Icon from '../../common/styles/Icon';
import IconTrain from './assets/icon-train.svg';
import { IconWrapper, SuggestionWrapper } from './SearchSuggestion.style';
import { WholeState } from '../../store/common/type';

const SUGGESTION_ICON_COLOR = '#697684';
const imageSharpieConfig = { width: 65, height: 43 };
const suggestionIconMap = {
  zone: 'mappin',
  metro_station: 'fa-subway',
  road: 'car',
  district: 'mappin',
};

interface Suggestion {
  type: string;
  title: string;
  subtitle?: string;
  image?: string;
}

interface SearchSuggestionProps {
  suggestions: Suggestion[];
  currentSourcePage: string;
}
const SearchSuggestion: React.FC<SearchSuggestionProps> = ({
  suggestions,
  currentSourcePage,
}) => {
  const [projects, locationSuggestions] = partition(suggestions, {
    type: 'project',
  });
  const isNextGenSupported = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.isNextGenSupported : false,
  );
  const userInfo = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.user : {},
  );
  const isTransportation = (type: string) => {
    return type === 'metro_station';
  };
  const trackSuggestion = (
    term: string,
    { projectName = '', locationName = '', locationType = '' },
  ) => {
    trackSelectFromSearchSuggestion(
      userInfo,
      currentSourcePage,
      term,
      projectName,
      locationName,
      locationType,
    );
  };

  const buildProjectSuggestionList = () =>
    !isEmpty(projects) && (
      <div className="suggestion-block search-list">
        <h4 className="block-title">projects</h4>
        {projects.map((project) => {
          const projectTitle = project.title.replace(/&amp;/g, '&');

          return (
            <Link
              key={projectTitle}
              href={`${searchPageUrlGenerator(project)}`}
              data-test={'suggestion-link'}
              onClick={() =>
                trackSuggestion('Project', { projectName: projectTitle })
              }
            >
              <div key={projectTitle} className="suggestion-item">
                <div className="item-thumb">
                  <img
                    src={handleImageUrl(
                      project.image,
                      imageSharpieConfig.width,
                      imageSharpieConfig.height,
                      isNextGenSupported,
                    )}
                    alt={projectTitle}
                  />
                </div>
                <div className="item-info-wrapper">
                  <h5 className="item-title">{projectTitle}</h5>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    );

  const buildLocationSuggestionList = () =>
    !isEmpty(locationSuggestions) && (
      <div className={'search-list'}>
        {locationSuggestions.map((suggestion) => (
          <Link
            key={suggestion.title}
            href={`${searchPageUrlGenerator(suggestion)}`}
            onClick={() =>
              trackSuggestion('Location', {
                locationName: suggestion.title,
                locationType: suggestion.type,
              })
            }
          >
            <div
              className="suggestion-item"
              key={suggestion.title}
              data-test="suggestion-link"
            >
              {isTransportation(suggestion.type) ? (
                <IconWrapper
                  className="item-icon"
                  dangerouslySetInnerHTML={{ __html: IconTrain }}
                />
              ) : (
                <Icon
                  className="item-icon"
                  iconName={
                    suggestionIconMap[
                      suggestion.type as keyof typeof suggestionIconMap
                    ]
                  }
                  color={SUGGESTION_ICON_COLOR}
                />
              )}
              <div className="item-info-wrapper">
                <h5 className="item-title">{suggestion.title}</h5>
                <p className="item-subtitle" data-test="subtitle">
                  {suggestion.subtitle || suggestion.type}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    );

  return (
    <SuggestionWrapper className="scroll-bar" data-test={'search-suggestion'}>
      {buildLocationSuggestionList()}
      {buildProjectSuggestionList()}
    </SuggestionWrapper>
  );
};

export default SearchSuggestion;
